/*
 * Geometry and placement rules for the workshop scheduler board (modelled on the CGL timeline: one
 * column per day covering only the hours anyone works, positions in px per hour, 15-minute snap).
 * Pure functions, so they can be tested and shared by the board and its drag handling.
 */

export const SNAP_MIN = 15;

/** The part of each day the board shows, in minutes from midnight. */
export interface DayRange {
  startMin: number;
  endMin: number;
}

/** Shown when nobody in view is working (what the board showed before working hours). */
export const DEFAULT_RANGE: DayRange = { startMin: 7 * 60, endMin: 18 * 60 };

/** A fitter's working hours on one date, as the server resolves them (night = 22:00-24:00 + next day 00:00-06:00). */
export interface FitterDay {
  windows: { startMin: number; endMin: number }[];
  closed: boolean;
  closedReason: string | null;
}

/** A fitter's hours on a date; null when not loaded (treated as not working). */
export type HoursLookup = (fitterId: number, date: string) => FitterDay | null;

export interface BoardBlock {
  id: number;
  fitterId: number;
  /** YYYY-MM-DD */
  date: string;
  /** Minutes from midnight. */
  startMin: number;
  durMin: number;
  jobType: 'defect' | 'inspection';
  status: 'planned' | 'done';
  registration: string;
  title: string;
  colour: string;
  actualHours: number | null;
  notes: string | null;
  workshopDefectId: number | null;
  vehicleInspectionId: number | null;
}

export interface BoardAbsence {
  fitterId: number;
  startDate: string;
  endDate: string;
  typeName: string | null;
}

export interface Candidate {
  fitterId: number;
  date: string;
  startMin: number;
  durMin: number;
}

export interface Verdict {
  ok: boolean;
  message: string;
}

export function snapMin(min: number): number {
  return Math.round(min / SNAP_MIN) * SNAP_MIN;
}

/**
 * Earliest start to latest end of everyone's hours in view, rounded out to whole hours - widened to take in
 * any block in view booked outside them (hours changed after it was booked), so it is never drawn off its day.
 */
export function boardRange(days: FitterDay[], blocks: { startMin: number; durMin: number }[] = []): DayRange {
  const windows = [...days.flatMap((d) => d.windows), ...blocks.map((b) => ({ startMin: b.startMin, endMin: b.startMin + b.durMin }))];
  if (!windows.length) {
    return DEFAULT_RANGE;
  }
  return {
    startMin: Math.floor(Math.min(...windows.map((w) => w.startMin)) / 60) * 60,
    endMin: Math.ceil(Math.max(...windows.map((w) => w.endMin)) / 60) * 60,
  };
}

/** The parts of the range a fitter is not working that day, for shading. */
export function offHours(day: FitterDay, range: DayRange): { startMin: number; endMin: number }[] {
  const out: { startMin: number; endMin: number }[] = [];
  let at = range.startMin;
  for (const w of [...day.windows].sort((a, b) => a.startMin - b.startMin)) {
    if (w.startMin > at) {
      out.push({ startMin: at, endMin: Math.min(w.startMin, range.endMin) });
    }
    at = Math.max(at, w.endMin);
  }
  if (at < range.endMin) {
    out.push({ startMin: at, endMin: range.endMin });
  }
  return out.filter((g) => g.endMin > g.startMin);
}

/** Left offset in px of a time, across the visible day columns. */
export function xOf(days: string[], date: string, min: number, pxPerHour: number, range: DayRange): number {
  const index = days.indexOf(date);
  return index * ((range.endMin - range.startMin) / 60) * pxPerHour + ((min - range.startMin) / 60) * pxPerHour;
}

/** Day and minutes under an x offset (px from the track's left edge), or null outside the board. */
export function minutesAtX(days: string[], x: number, pxPerHour: number, range: DayRange): { date: string; min: number } | null {
  const dayWidth = ((range.endMin - range.startMin) / 60) * pxPerHour;
  const index = Math.floor(x / dayWidth);
  if (x < 0 || index >= days.length) {
    return null;
  }
  const within = x - index * dayWidth;
  return { date: days[index], min: range.startMin + (within / pxPerHour) * 60 };
}

export function hhmm(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

/** Whether minutes [start, end) sit inside one of the windows (windows that touch join up). */
export function insideHours(day: FitterDay | null, startMin: number, endMin: number): boolean {
  if (!day) {
    return false;
  }
  const joined: { startMin: number; endMin: number }[] = [];
  for (const w of [...day.windows].sort((a, b) => a.startMin - b.startMin)) {
    const last = joined[joined.length - 1];
    if (last && w.startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, w.endMin);
    } else {
      joined.push({ ...w });
    }
  }
  return joined.some((w) => w.startMin <= startMin && endMin <= w.endMin);
}

/**
 * Whether a block can go here: at least 15 minutes, not on the fitter's absence day, inside their working
 * hours that day, and not over another of the fitter's blocks (the one being moved, `ignoreId`, excepted).
 * The server applies the same rules; this gives the live green/red preview.
 */
export function placementVerdict(blocks: BoardBlock[], absences: BoardAbsence[], hours: HoursLookup, c: Candidate, ignoreId: number | null): Verdict {
  if (c.durMin < SNAP_MIN) {
    return { ok: false, message: 'At least 15 minutes' };
  }
  const absence = absences.find((a) => a.fitterId === c.fitterId && a.startDate <= c.date && a.endDate >= c.date);
  if (absence) {
    return { ok: false, message: 'Absent: ' + (absence.typeName || 'away') };
  }
  const day = hours(c.fitterId, c.date);
  if (!day || day.closed || !day.windows.length) {
    return { ok: false, message: 'Not working' + (day && day.closedReason ? ': ' + day.closedReason : '') };
  }
  if (!insideHours(day, c.startMin, c.startMin + c.durMin)) {
    return { ok: false, message: `Outside hours (${day.windows.map((w) => `${hhmm(w.startMin)}–${hhmm(w.endMin)}`).join(', ')})` };
  }
  const clash = blocks.find((b) => b.id !== ignoreId && b.fitterId === c.fitterId && b.date === c.date
    && b.startMin < c.startMin + c.durMin && b.startMin + b.durMin > c.startMin);
  if (clash) {
    return { ok: false, message: `Overlaps ${clash.registration} – ${clash.title} (${hhmm(clash.startMin)}–${hhmm(clash.startMin + clash.durMin)})` };
  }
  return { ok: true, message: '' };
}
